import axios from "axios";
import { WEBOT_SERVER_ENDPOINT } from "../logic/Consts";
import { firebaseApp, db } from "../firebaseConfig";
import {
  SignatureTemplate,
  SignatureTemplateBundle,
  SignatureDocument,
  SignatureDocumentStatus,
  SignatureNameTag,
  SignerDraftInput
} from "../@types/signatures";
import { SIGNATURE_DOCUMENTS_COLLECTION_NAME, REST_schema } from "../@types/sharedSchema";
import { GenericConverter } from "../utils/firestoreConverters";

const SIGNATURES_API_URL = `${WEBOT_SERVER_ENDPOINT}/api/v1/signatures`;

const http = axios.create({ baseURL: SIGNATURES_API_URL });

http.interceptors.request.use(async (config) => {
  try {
    const currentUser = firebaseApp.auth().currentUser;
    if (currentUser) {
      const token = await currentUser.getIdToken();
      config.headers.Authorization = token;
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error getting Firebase auth token for signatures API:", error);
  }
  return config;
});

export const skipSignatureOtpInPerson = async (slug: string): Promise<{ otpAlreadyVerified: boolean; otpSkippedInPerson: boolean }> => {
  const { data } = await http.post(`/in-person/${encodeURIComponent(slug)}/skip-otp`, {
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  });
  return data;
};

/**
 * Signing a SET of contracts in person: where the set of this link stands, for its OWNER only (the
 * server answers nobody else, and the public sender lookup never carries it). `next` is the contract
 * to open next on the realtor's device — on its ordinary link, where the code is skipped only by the
 * owner's explicit choice for THAT contract.
 */
export type InPersonSigningSet = {
  total: number;
  signed: number;
  next?: { slug: string; title: string; formattedNumber: string };
};

export const getInPersonSigningEligibility = async (slug: string): Promise<{ canSkipOtp: boolean; set?: InPersonSigningSet }> => {
  const { data } = await http.get(`/in-person/${encodeURIComponent(slug)}`);
  return data;
};

// ===================== TEMPLATES =====================

// Session cache for the send-form pickers (templates + bundles). Opening the
// "new signature" dialog used to block on a fresh /templates round-trip every
// time; the globally-mounted dialog now PRELOADS both lists right after app
// start and the form renders from cache instantly, then silently revalidates
// (stale-while-revalidate — a just-edited template still shows up correctly).
let templatesCache: SignatureTemplate[] | null = null;
let templatesInflight: Promise<SignatureTemplate[]> | null = null;
let bundlesCache: SignatureTemplateBundle[] | null = null;
let bundlesInflight: Promise<SignatureTemplateBundle[]> | null = null;
// Default templates per signing scenario. Cached on the same
// stale-while-revalidate basis as the picker data: the send form's role
// quick-pick needs it on every open to resolve the picked role's template,
// and the settings dialog seeds its rows from it.
let defaultTemplatesCache: REST_schema.GetSignatureDefaultTemplatesResponse | null = null;
let defaultTemplatesInflight: Promise<REST_schema.GetSignatureDefaultTemplatesResponse> | null = null;
// Marketplace (Webot + org sections). Cached so the settings dialog can name
// a default that points at a system template the user never favorited, and so
// the admin section can offer the Webot catalogue, without a fresh round-trip
// on every open.
let marketplaceCache: MarketplaceData | null = null;
let marketplaceInflight: Promise<MarketplaceData> | null = null;
// Templates fetched individually by id. The send form's role quick-pick points
// at templates that are usually NOT in the user's own list (Webot system
// templates they never favorited), so without this every role click paid for a
// fetch per document before it could even name them. `preloadSignatureSendData`
// warms the ids the quick-pick can reach, making the click instant.
const templatesByIdCache: Record<string, SignatureTemplate> = {};
const templateByIdInflight: Record<string, Promise<SignatureTemplate>> = {};

export const getCachedTemplates = (): SignatureTemplate[] | null => templatesCache;
export const getCachedBundles = (): SignatureTemplateBundle[] | null => bundlesCache;
/** A template previously fetched by id this session, if any. */
export const getCachedTemplateById = (templateId: string): SignatureTemplate | null =>
  templatesByIdCache[templateId] ?? null;
export const getCachedSignatureDefaultTemplates =
  (): REST_schema.GetSignatureDefaultTemplatesResponse | null => defaultTemplatesCache;
export const getCachedMarketplaceTemplates = (): MarketplaceData | null => marketplaceCache;

/**
 * Warm the by-id cache with every template the role quick-pick can select.
 * Without this, picking a role blocks on one fetch per document (two for
 * seller + exclusivity) before the form can show what's being sent.
 */
const warmDefaultTemplates = (settings: REST_schema.GetSignatureDefaultTemplatesResponse): void => {
  const ids = new Set(Object.values(settings.effective).filter((id): id is string => !!id));
  ids.forEach((id) => void getTemplate(id).catch(() => undefined));
};

/**
 * Fire-and-forget warm-up of the send-form picker data. Safe to call
 * repeatedly. Includes the default templates because the role quick-pick
 * needs them the moment the send dialog opens.
 */
export const preloadSignatureSendData = (): void => {
  void listTemplates().catch(() => undefined);
  void listBundles().catch(() => undefined);
  void getSignatureDefaultTemplates().then(warmDefaultTemplates).catch(() => undefined);
};

/**
 * Warm-up for the signature settings dialog. Called when the cog becomes
 * visible (not at app start) — the marketplace is only needed by that dialog,
 * so this keeps it off the critical path for users who never open it while
 * still making the open feel instant.
 */
export const preloadSignatureSettingsData = (): void => {
  void listTemplates().catch(() => undefined);
  void getSignatureDefaultTemplates().catch(() => undefined);
  void listMarketplaceTemplates().catch(() => undefined);
};

export const listTemplates = async (): Promise<SignatureTemplate[]> => {
  if (templatesInflight) return templatesInflight;
  templatesInflight = http
    .get<{ data: SignatureTemplate[] }>("/templates")
    .then(({ data }) => {
      templatesCache = data.data;
      return data.data;
    })
    .finally(() => {
      templatesInflight = null;
    });
  return templatesInflight;
};

/**
 * Always network-fresh (callers like the editor depend on that), but
 * inflight-deduped so concurrent requests for the same id share one response,
 * and it populates the by-id cache below.
 */
export const getTemplate = async (templateId: string): Promise<SignatureTemplate> => {
  const inflight = templateByIdInflight[templateId];
  if (inflight) return inflight;
  templateByIdInflight[templateId] = http
    .get<SignatureTemplate>(`/templates/${templateId}`)
    .then(({ data }) => {
      templatesByIdCache[templateId] = data;
      return data;
    })
    .finally(() => {
      delete templateByIdInflight[templateId];
    });
  return templateByIdInflight[templateId];
};

export const createTemplate = async (input: {
  name: string;
  description?: string;
}): Promise<SignatureTemplate> => {
  const { data } = await http.post<SignatureTemplate>("/templates", input);
  return data;
};

export const updateTemplate = async (
  templateId: string,
  update: Partial<
    Pick<
      SignatureTemplate,
      | "name"
      | "customerFacingName"
      | "description"
      | "fields"
      | "listingsBlocks"
      | "additionalTermsBlock"
      | "signerRoles"
      | "content"
      | "visibility"
    >
  > & {
    // `null` explicitly clears the tag; `undefined` leaves it untouched.
    nameTag?: SignatureNameTag | null;
  }
): Promise<SignatureTemplate> => {
  const { data } = await http.patch<SignatureTemplate>(`/templates/${templateId}`, update);
  return data;
};

export const archiveTemplate = async (templateId: string): Promise<void> => {
  await http.delete(`/templates/${templateId}`);
};

// ===================== PER-USER TEMPLATE COUNTER =====================
// The "next document number" lives in its own collection, keyed by
// (userId, templateId), and has its own endpoints. Keeping it off the
// SignatureTemplate object makes it structurally impossible to write it back
// onto the template doc by mistake.

/** Returns a map from templateId → counter slice (next number + optional
 * prefix) for every template the caller has a counter for. Templates without
 * a counter are omitted; missing entries default to `{ nextDocumentNumber: 1 }`. */
export const listUserTemplateCounters = async (): Promise<Record<string, REST_schema.SignatureTemplateCounterDto>> => {
  const { data } = await http.get<REST_schema.ListSignatureTemplateCountersResponse>("/templates/user-counters");
  return data.counters ?? {};
};

/** Returns the counter slice for the requesting user on a specific template
 * (defaults to `{ nextDocumentNumber: 1 }`). Throws 404 if the user can't
 * view the template. */
export const getUserTemplateCounter = async (
  templateId: string
): Promise<REST_schema.GetSignatureTemplateCounterResponse> => {
  const { data } = await http.get<REST_schema.GetSignatureTemplateCounterResponse>(
    `/templates/${encodeURIComponent(templateId)}/user-counter`
  );
  return data;
};

/** Sets the counter slice for the requesting user on a specific template.
 * Pass an empty string for `numberPrefix` to clear; omit it to leave the
 * persisted prefix unchanged. The template doc itself is never written. */
export const setUserTemplateCounter = async (
  templateId: string,
  patch: REST_schema.SetSignatureTemplateCounterRequest
): Promise<REST_schema.SetSignatureTemplateCounterResponse> => {
  const { data } = await http.put<REST_schema.SetSignatureTemplateCounterResponse>(
    `/templates/${encodeURIComponent(templateId)}/user-counter`,
    patch
  );
  return data;
};

// ===================== DEFAULT TEMPLATES SETTINGS =====================
// Default template per signing scenario (`SignatureDefaultTemplateKeys`). Two
// layers: a Webot-admin-managed global map + per-user overrides. `effective`
// (user-over-global merge) is what the send flow uses.

/**
 * Three-layer view: { global, user, effective }. Cached + inflight-deduped —
 * callers that want an instant first paint read `getCachedSignatureDefaultTemplates()`
 * and let this revalidate in the background.
 */
export const getSignatureDefaultTemplates =
  async (): Promise<REST_schema.GetSignatureDefaultTemplatesResponse> => {
    if (defaultTemplatesInflight) return defaultTemplatesInflight;
    defaultTemplatesInflight = http
      .get<REST_schema.GetSignatureDefaultTemplatesResponse>("/settings/default-templates")
      .then(({ data }) => {
        defaultTemplatesCache = data;
        return data;
      })
      .finally(() => {
        defaultTemplatesInflight = null;
      });
    return defaultTemplatesInflight;
  };

/**
 * Partial update of the CALLER'S personal overrides. String sets a template
 * id; null clears the override (falls back to the global default). Returns
 * the refreshed three-layer view.
 */
export const setUserSignatureDefaultTemplates = async (
  defaultTemplates: REST_schema.SetSignatureDefaultTemplatesRequest["defaultTemplates"]
): Promise<REST_schema.SetSignatureDefaultTemplatesResponse> => {
  const { data } = await http.put<REST_schema.SetSignatureDefaultTemplatesResponse>(
    "/settings/default-templates",
    { defaultTemplates }
  );
  // The response IS the post-write three-layer view — seed the cache with it
  // so the next send-form open picks up the new defaults without a refetch,
  // and warm any newly-referenced template so the next role click stays instant.
  defaultTemplatesCache = data;
  warmDefaultTemplates(data);
  return data;
};

/**
 * Webot-admin only: partial update of the GLOBAL defaults every user inherits.
 * The server enforces admin gating regardless of this client call.
 */
export const setGlobalSignatureDefaultTemplates = async (
  defaultTemplates: REST_schema.SetSignatureDefaultTemplatesRequest["defaultTemplates"]
): Promise<REST_schema.SetSignatureDefaultTemplatesResponse> => {
  const { data } = await http.put<REST_schema.SetSignatureDefaultTemplatesResponse>(
    "/settings/default-templates/global",
    { defaultTemplates }
  );
  defaultTemplatesCache = data;
  warmDefaultTemplates(data);
  return data;
};

// ===================== MARKETPLACE / FAVORITES / DUPLICATE =====================

/** What the marketplace endpoint returns. Sections come pre-grouped from the server. */
export type MarketplaceData = {
  webot: SignatureTemplate[];
  organizations: Array<{
    org: { id: string; name: string; logo?: string };
    templates: SignatureTemplate[];
  }>;
};

/**
 * Cached + inflight-deduped (same stale-while-revalidate contract as
 * `listTemplates`) — the marketplace page still gets fresh data on mount
 * because it awaits this call; the settings dialog paints from
 * `getCachedMarketplaceTemplates()` first.
 */
export const listMarketplaceTemplates = async (): Promise<MarketplaceData> => {
  if (marketplaceInflight) return marketplaceInflight;
  marketplaceInflight = http
    .get<MarketplaceData>("/templates/marketplace")
    .then(({ data }) => {
      marketplaceCache = data;
      return data;
    })
    .finally(() => {
      marketplaceInflight = null;
    });
  return marketplaceInflight;
};

/**
 * Persist a drag & drop reorder of one marketplace section. Omit
 * `organizationId` for the Webot (system) section — Webot admins only; pass
 * it for an org section — org admins (or Webot admins). The server enforces
 * authorization regardless of this client call.
 */
export const reorderMarketplaceTemplates = async (
  orderedTemplateIds: string[],
  organizationId?: string
): Promise<void> => {
  const body: REST_schema.ReorderMarketplaceTemplatesRequest = {
    orderedTemplateIds,
    organizationId
  };
  await http.put("/templates/marketplace-order", body);
};

export const favoriteTemplate = async (templateId: string): Promise<void> => {
  await http.post(`/templates/${templateId}/favorite`);
};

export const unfavoriteTemplate = async (templateId: string): Promise<void> => {
  await http.delete(`/templates/${templateId}/favorite`);
};

export const duplicateTemplate = async (
  templateId: string,
  options?: { name?: string }
): Promise<SignatureTemplate> => {
  const { data } = await http.post<SignatureTemplate>(
    `/templates/${templateId}/duplicate`,
    options ?? {}
  );
  return data;
};

/**
 * Admin-only: publishes the given template as a Webot system default. The
 * server enforces admin gating regardless of this client call.
 */
export const publishTemplateAsSystem = async (
  templateId: string,
  options?: { name?: string }
): Promise<SignatureTemplate> => {
  const { data } = await http.post<SignatureTemplate>(
    `/templates/${templateId}/publish-as-system`,
    options ?? {}
  );
  return data;
};

// ===================== TEMPLATE BUNDLES =====================
// Reusable, named groups of templates a realtor sends together. Server CRUD
// lives at `/signatures/bundles/*`; the send endpoint at `/bundles/send`
// receives one entry per template (in bundle order) and creates one
// SignatureDocument per template tagged with the bundle id.

export const listBundles = async (): Promise<SignatureTemplateBundle[]> => {
  if (bundlesInflight) return bundlesInflight;
  bundlesInflight = http
    .get<REST_schema.ListSignatureBundlesResponse>("/bundles")
    .then(({ data }) => {
      bundlesCache = (data.bundles as unknown) as SignatureTemplateBundle[];
      return bundlesCache;
    })
    .finally(() => {
      bundlesInflight = null;
    });
  return bundlesInflight;
};

/** Public + org-shared bundles for the marketplace. Mirrors `MarketplaceData` but for bundles. */
export type BundlesMarketplaceData = {
  public: SignatureTemplateBundle[];
  organizations: Array<{
    org: { id: string; name: string; logo?: string };
    bundles: SignatureTemplateBundle[];
  }>;
};

export const listMarketplaceBundles = async (): Promise<BundlesMarketplaceData> => {
  const { data } = await http.get<BundlesMarketplaceData>("/bundles/marketplace");
  return data;
};

export const getBundle = async (bundleId: string): Promise<SignatureTemplateBundle> => {
  const { data } = await http.get<REST_schema.GetSignatureBundleResponse>(
    `/bundles/${encodeURIComponent(bundleId)}`
  );
  return (data as unknown) as SignatureTemplateBundle;
};

export const createBundle = async (
  input: REST_schema.CreateSignatureBundleRequest
): Promise<SignatureTemplateBundle> => {
  const { data } = await http.post<REST_schema.SignatureBundleDto>("/bundles", input);
  return (data as unknown) as SignatureTemplateBundle;
};

export const updateBundle = async (
  bundleId: string,
  update: REST_schema.UpdateSignatureBundleRequest
): Promise<SignatureTemplateBundle> => {
  const { data } = await http.patch<REST_schema.SignatureBundleDto>(
    `/bundles/${encodeURIComponent(bundleId)}`,
    update
  );
  return (data as unknown) as SignatureTemplateBundle;
};

export const archiveBundle = async (bundleId: string): Promise<void> => {
  await http.delete(`/bundles/${encodeURIComponent(bundleId)}`);
};

export const favoriteBundle = async (bundleId: string): Promise<void> => {
  await http.post(`/bundles/${encodeURIComponent(bundleId)}/favorite`);
};

export const unfavoriteBundle = async (bundleId: string): Promise<void> => {
  await http.delete(`/bundles/${encodeURIComponent(bundleId)}/favorite`);
};

export type DuplicateBundleResponse = {
  bundle: SignatureTemplateBundle;
  /** Source template ids the caller can't view, omitted from the new bundle. */
  skippedTemplateIds: string[];
  reusedTemplateCount: number;
  clonedTemplateCount: number;
};

/**
 * Duplicate a marketplace bundle into the caller's account. The server
 * smart-dedups per-template: existing clones / owned templates are reused;
 * accessible-but-not-owned templates are cloned; inaccessible ones are
 * skipped (surfaced in `skippedTemplateIds` so the UI can warn).
 */
export const duplicateBundle = async (
  bundleId: string,
  options?: { name?: string }
): Promise<DuplicateBundleResponse> => {
  const { data } = await http.post<DuplicateBundleResponse>(
    `/bundles/${encodeURIComponent(bundleId)}/duplicate`,
    options ?? {}
  );
  return data;
};

/**
 * Send a bundle. The caller has already deduped `(field.name, signerRoleId)`
 * across the bundle's templates client-side, projected the deduped values
 * back into each template's `fieldValues`, then rendered HTML + rasterised
 * PDF per template (same renderer as the single-doc send). The server
 * validates `perDocument` matches `bundle.templateIds` exactly and runs the
 * send pipeline per doc in parallel — partial failures leave the failed
 * doc in `status: 'draft'` for manual retry.
 */
export const sendBundle = async (
  payload: REST_schema.SendSignatureBundleRequest
): Promise<Omit<REST_schema.SendSignatureBundleResponse, "documents"> & { documents: SignatureDocument[] }> => {
  const { data } = await http.post<Omit<REST_schema.SendSignatureBundleResponse, "documents"> & { documents: SignatureDocument[] }>(
    "/bundles/send",
    payload
  );
  return data;
};

// ===================== DOCUMENTS =====================

export const listDocuments = async (params?: {
  contactId?: string;
  listingId?: string;
  status?: SignatureDocumentStatus;
  bundleId?: string;
}): Promise<SignatureDocument[]> => {
  const { data } = await http.get<{ data: SignatureDocument[] }>("/documents", { params });
  return data.data;
};

export const getDocument = async (documentId: string): Promise<SignatureDocument> => {
  const { data } = await http.get<SignatureDocument>(`/documents/${documentId}`);
  return data;
};

export const createDocument = async (input: {
  templateId: string;
  contactIds: string[];
  listingIds: string[];
  signers: SignerDraftInput[];
  notes?: string;
  /**
   * Explicit document number for this document instead of the caller's next
   * auto number (positive integer). The per-user counter then continues from
   * `max(currentNext, override + 1)` server-side.
   */
  documentNumberOverride?: number;
}): Promise<SignatureDocument> => {
  const { data } = await http.post<SignatureDocument>("/documents", input);
  return data;
};

export const updateDocument = async (
  documentId: string,
  update: Partial<Pick<SignatureDocument, "contactIds" | "listingIds" | "signers" | "notes">>
): Promise<SignatureDocument> => {
  const { data } = await http.patch<SignatureDocument>(`/documents/${documentId}`, update);
  return data;
};

export const archiveDocument = async (documentId: string): Promise<void> => {
  await http.delete(`/documents/${documentId}`);
};

/**
 * Send a document. The caller has already rasterized the template HTML into
 * a PDF in the browser (see `signaturesHtmlToPdf.ts`) and extracted the
 * field positions during the same render. The backend stores the PDF +
 * fields and mints the per-signer signing slugs.
 *
 * `html` is sent alongside as a debug snapshot so the server can persist it
 * on `sentHtml` — used by the ad-hoc re-render scripts; not by any user-
 * facing UI.
 */
export const sendDocument = async (
  documentId: string,
  payload: REST_schema.SendSignatureDocumentRequest
): Promise<SignatureDocument> => {
  const { data } = await http.post<SignatureDocument>(
    `/documents/${documentId}/send`,
    payload
  );
  return data;
};

/** Download the archived contract with the sender's editable text prefills for printing. */
export const downloadPreparedPdfBlob = async (documentId: string): Promise<Blob> => {
  const { data } = await http.get<Blob>(`/documents/${encodeURIComponent(documentId)}/prepared-pdf`, {
    responseType: "blob"
  });
  return data;
};

/**
 * Download the signed PDF through the AUTHENTICATED owner endpoint. For a
 * completed document this is the stored composed PDF; for a partial document
 * (e.g. hidden-addresses main signed, addendum pending) the server composes
 * the interim "signed so far" main document on the fly — the public stream
 * endpoint only serves completed docs, so this is the only path for partials.
 *
 * `type: "audit"` streams the standalone "Certificate of Electronic Signature".
 * It is served ONLY through this authenticated endpoint (never bundled into the
 * signed PDF nor exposed on the public signer-facing viewer), so the signer
 * never sees it — only the realtor can download it.
 */
export const downloadSignedPdfBlob = async (
  documentId: string,
  type?: "signed" | "audit"
): Promise<Blob> => {
  const { data } = await http.get<Blob>(`/documents/${documentId}/signed-pdf`, {
    params: type ? { type } : undefined,
    responseType: "blob"
  });
  return data;
};

export const resendWhatsapp = async (
  documentId: string,
  signerId: string
): Promise<SignatureDocument> => {
  const { data } = await http.post<SignatureDocument>(
    `/documents/${documentId}/signers/${signerId}/resend-whatsapp`
  );
  return data;
};

/**
 * The automatic send of a SET of contracts (one bundle preparation) as ONE message: one WhatsApp +
 * one SMS with ONE link, the first contract the client still has to sign; the sign page lists the
 * rest after the code. Any member's id sends the whole set; a document outside a set is sent alone.
 * Every contract it covers comes back stamped with that delivery (server: signatureSetSend.service.ts).
 */
export const sendSigningSet = async (
  documentId: string,
  signerId: string
): Promise<Omit<REST_schema.SendSignatureSetResponse, "documents"> & { documents: SignatureDocument[] }> => {
  const { data } = await http.post<Omit<REST_schema.SendSignatureSetResponse, "documents"> & { documents: SignatureDocument[] }>(
    `/documents/${encodeURIComponent(documentId)}/signers/${encodeURIComponent(signerId)}/send-set`
  );
  return data;
};

/**
 * Display-level document status: the stored statuses plus the derived
 * `"created"` — a document whose send suppressed signer notifications
 * (`notifySigners: false`), so the ceremony is live but the customer was
 * never messaged.
 */
export type SignatureDocumentDisplayStatus = SignatureDocumentStatus | "created";

/**
 * What the realtor should read as the document's state. The server stamps
 * `status: 'sent'` when the send pipeline finalizes (PDF archived, signing
 * links minted) whether or not anyone was messaged — so a send with
 * notifications off would render "נשלח" while the customer got nothing.
 *
 * Derived, not stored: a 'sent' document where every signer is still
 * 'pending' with ZERO WhatsApp attempts was never messaged (a FAILED attempt
 * records attemptCount >= 1, so this can't misread a delivery failure as
 * "not sent"). It flips to 'sent' automatically once the realtor messages a
 * signer via "resend WhatsApp" or any signer progresses.
 */
export const displayDocumentStatus = (doc: {
  status: SignatureDocumentStatus;
  signers: Array<{ status?: string; whatsappDelivery?: { attemptCount?: number } }>;
}): SignatureDocumentDisplayStatus =>
  doc.status === "sent" &&
  doc.signers.length > 0 &&
  doc.signers.every(
    (s) => s.status === "pending" && (s.whatsappDelivery?.attemptCount ?? 0) === 0
  )
    ? "created"
    : doc.status;

/**
 * Whether a document is still editable after being sent: it must be a
 * native-engine document in `status: 'sent'` with ZERO signing progress —
 * no signer has signed (any phase), declined, or completed. Mirrors the
 * server-side guard in `resendEditedDocument`; the UI uses this to decide
 * whether to show the "edit document" action.
 */
export const canEditSentDocument = (doc: SignatureDocument): boolean =>
  doc.status === "sent" &&
  doc.signingEngine === "native" &&
  !doc.archivedAt &&
  doc.signers.every(
    (s) =>
      s.status !== "signed" &&
      s.status !== "declined" &&
      !s.signedAt &&
      !s.mainSignedAt &&
      !s.addendumSignedAt &&
      !s.questionnaireSignedAt &&
      !!s.signing?.slug
  );

/**
 * Whether the primary signer's signing link is still worth handing out — i.e.
 * whether the "share link" and "resend on WhatsApp" actions should be offered
 * at all. Both actions target `signers[0]`, so once that signer has signed (or
 * the document reached a terminal state) the link leads to a finished ceremony
 * and the buttons are noise. Callers still check for the concrete
 * `publicSigningUrl` / `slug` they need on top of this gate.
 */
export const isSigningLinkLive = (doc: SignatureDocument): boolean => {
  if (doc.status === "completed" || doc.status === "declined" || doc.status === "expired") {
    return false;
  }
  const primary = doc.signers[0];
  if (!primary) return false;
  return primary.status !== "signed" && primary.status !== "declined" && !primary.signedAt;
};

/**
 * Edit-and-resend a SENT (but not yet signed) document. Same rendered-artifact
 * payload as `sendDocument`, plus optional signer/notes updates. The server
 * replaces the sent artifacts in place, keeps every signer's signing link
 * valid, and notifies each signer by SMS that the document was edited and is
 * ready to be signed.
 */
export const editSentDocument = async (
  documentId: string,
  payload: REST_schema.EditSignatureDocumentRequest
): Promise<SignatureDocument> => {
  const { data } = await http.post<SignatureDocument>(
    `/documents/${documentId}/edit-resend`,
    payload
  );
  return data;
};

// ===================== ADMIN (cross-customer audit) =====================
// Reached only by Webot staff — the server enforces admin gating on these
// routes (validateWebotAdmin) regardless of this client call. Unlike the
// owner-scoped document calls above, these read across ALL customers. The
// signed-PDF download streams through the server so it carries the admin's
// Firebase token via the `http` interceptor — opening the raw storage URL in
// a new tab couldn't attach auth.

export const adminListSignatureDocuments = async (
  params: REST_schema.AdminListSignatureDocumentsRequest
): Promise<{ documents: SignatureDocument[]; nextCursor: string | null }> => {
  const { data } = await http.get<REST_schema.AdminListSignatureDocumentsResponse>("/admin/documents", {
    params
  });
  // Server dates serialize to ISO strings; the client `SignatureDocument` view
  // types them as strings — same cast the bundle/document calls above use.
  return {
    documents: (data.documents as unknown) as SignatureDocument[],
    nextCursor: data.nextCursor
  };
};

export const adminGetSignatureDocument = async (documentId: string): Promise<SignatureDocument> => {
  const { data } = await http.get<SignatureDocument>(`/admin/documents/${encodeURIComponent(documentId)}`);
  return data;
};

/**
 * Download the signed PDF (`type: "signed"`) or the audit-log PDF
 * (`type: "audit"`) for any document, regardless of owner. Returns a Blob the
 * caller turns into an object URL to open / download.
 */
export const adminDownloadSignatureDocumentBlob = async (
  documentId: string,
  type: "signed" | "audit"
): Promise<Blob> => {
  const { data } = await http.get<Blob>(`/admin/documents/${encodeURIComponent(documentId)}/signed-pdf`, {
    params: { type },
    responseType: "blob"
  });
  return data;
};

// ===================== OFFICE ADMIN (read-only view of the agents' documents) =====================
// For a caller the server finds in `dashboard-orgs.admins`: the signature documents owned by
// the members of the offices they administer. GET only — nothing under `/org/` sends, edits or
// archives — and the server re-resolves the roster on every request, so anyone else gets 403
// (the list) or 404 (anything by id) whatever this client asks for.
//
// The payload is `REST_schema.OrgSignatureDocument`, an ALLOWLIST: no `signers[].signing`, no
// notes, no storage URL, and `has*` flags where the owner's document carries the files. It is
// NOT assignable to `SignatureDocument` and must never be cast to it: the cast compiles, and
// then every gate written against `signedDocument?.url` / `sentPdfUrl` silently reads "nothing
// to download". Consumers render from these two views instead.

type OrgSignerDateField =
  | "sentAt"
  | "viewedAt"
  | "signedAt"
  | "declinedAt"
  | "otpVerifiedAt"
  | "mainSignedAt"
  | "addendumSignedAt"
  | "questionnaireSignedAt";

/** Client view of `REST_schema.OrgSignatureDocumentSigner` — dates arrive as ISO strings. */
export type OrgSignatureDocumentSignerView = Omit<
  REST_schema.OrgSignatureDocumentSigner,
  OrgSignerDateField | "whatsappDelivery" | "inPersonOtpSkip"
> &
  Partial<Record<OrgSignerDateField, string>> & {
    whatsappDelivery: Omit<
      REST_schema.OrgSignatureDocumentSigner["whatsappDelivery"],
      "lastAttemptAt"
    > & { lastAttemptAt?: string };
    inPersonOtpSkip?: { at: string };
  };

/** Client view of `REST_schema.OrgSignatureDocument` — same dates-as-strings divergence. */
export type OrgSignatureDocumentView = Omit<
  REST_schema.OrgSignatureDocument,
  "id" | "createdTime" | "updatedTime" | "sentAt" | "completedAt" | "editedAt" | "signers"
> & {
  id: string;
  createdTime?: string;
  updatedTime?: string;
  sentAt?: string;
  completedAt?: string;
  editedAt?: string;
  signers: OrgSignatureDocumentSignerView[];
};

/**
 * One page of the office's documents, newest first. Drafts and archived documents are never
 * returned, whatever the filters, and there is no `search` parameter — the page searches the
 * rows it has loaded.
 *
 * A page may come back SHORTER than `limit` (even empty) with a non-null `nextCursor`: the
 * cursor is the last row the server examined, not the last one it returned. Only a null
 * cursor means the source is exhausted. `ownerUserIds` is the roster in scope, for the agent
 * filter — the client never guesses who is in the office.
 */
export const listOrgSignatureDocuments = async (
  params: REST_schema.OrgListSignatureDocumentsRequest
): Promise<{
  documents: OrgSignatureDocumentView[];
  nextCursor: string | null;
  ownerUserIds: string[];
}> => {
  const { data } = await http.get<REST_schema.OrgListSignatureDocumentsResponse>(
    "/org/documents",
    { params }
  );
  return {
    // Dates only: the wire carries ISO strings where the shared type says `Date`.
    documents: (data.documents as unknown) as OrgSignatureDocumentView[],
    nextCursor: data.nextCursor,
    ownerUserIds: data.ownerUserIds ?? []
  };
};

/** One colleague's document, once — there is no listener for a document the caller does not own. */
export const getOrgSignatureDocument = async (
  documentId: string
): Promise<OrgSignatureDocumentView> => {
  const { data } = await http.get<REST_schema.OrgGetSignatureDocumentResponse>(
    `/org/documents/${encodeURIComponent(documentId)}`
  );
  return (data as unknown) as OrgSignatureDocumentView;
};

/**
 * A colleague's signed PDF (`"signed"` — for a `partial` document the server composes the
 * "signed so far" interim), the audit certificate (`"audit"`) or the completed seller
 * questionnaire (`"questionnaire"`).
 *
 * ALWAYS this endpoint for a colleague's file, never the public by-id URL
 * (`getDocumentFileUrl`): that one runs no access check and writes no audit line.
 */
export const downloadOrgSignedPdfBlob = async (
  documentId: string,
  type: "signed" | "audit" | "questionnaire"
): Promise<Blob> => {
  const { data } = await http.get<Blob>(
    `/org/documents/${encodeURIComponent(documentId)}/signed-pdf`,
    {
      params: type === "signed" ? undefined : { type },
      responseType: "blob"
    }
  );
  return data;
};

/** A colleague's document as it was sent, before any signature. */
export const downloadOrgPreparedPdfBlob = async (documentId: string): Promise<Blob> => {
  const { data } = await http.get<Blob>(
    `/org/documents/${encodeURIComponent(documentId)}/prepared-pdf`,
    { responseType: "blob" }
  );
  return data;
};

/**
 * Normalise Firestore Timestamp values to ISO strings so downstream code
 * (sorting, chip display, REST round-trips) can treat every timestamp the
 * same way regardless of whether it arrived via the REST response (already
 * ISO) or the onSnapshot path (native Timestamp instances).
 *
 * Walks the doc recursively and swaps any value whose shape matches the
 * Firestore Timestamp surface (`.toDate()`). Native Date objects get the
 * same treatment for safety. Everything else passes through.
 */
const normalizeFirestoreTimestamps = <T>(value: T): T => {
  if (value === null || value === undefined) return value;
  if (value instanceof Date) return value.toISOString() as unknown as T;
  // Firestore Timestamp — duck-type on `.toDate()` so we don't need to
  // import the Firebase SDK type here (keeps the service file thin).
  if (typeof (value as any).toDate === "function") {
    try {
      return (value as any).toDate().toISOString() as unknown as T;
    } catch {
      return value;
    }
  }
  if (Array.isArray(value)) {
    return value.map((v) => normalizeFirestoreTimestamps(v)) as unknown as T;
  }
  if (typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = normalizeFirestoreTimestamps(v);
    }
    return out as T;
  }
  return value;
};

export type SubscribeToDocumentsOptions = {
  status?: SignatureDocumentStatus;
  templateId?: string;
  dateFrom?: Date;
  dateTo?: Date;
  /** Hard cap on returned rows. Used by the sent-list for lazy loading. */
  limit?: number;
};

/**
 * Firestore realtime subscription for the authenticated user's signature
 * documents. Used by the "sent" list so signer status / WhatsApp delivery
 * updates arrive without polling or refreshes — important because the send
 * flow is fire-and-forget, so the list needs to reflect backend progress.
 *
 * Filters + limit are pushed into the Firestore query so the page can avoid
 * loading the user's entire history when a filter is active or a small
 * page size is requested. The caller grows `limit` for "load more".
 *
 * Returns an unsubscribe function. Assumes Firestore rules allow the user
 * to read documents where ownerUserId matches their email.
 */
export const subscribeToDocuments = (
  ownerUserId: string,
  options: SubscribeToDocumentsOptions,
  onChange: (documents: SignatureDocument[]) => void,
  onError?: (error: Error) => void
): (() => void) => {
  // withConverter injects snap.id (and snap.ref.path) into each returned
  // object and converts top-level Firestore Timestamps to JS Dates. Without
  // it, raw snap.data() returns documents with no `id` field — and row-
  // click handlers wire `undefined` through everywhere. Converter behaviour
  // matches server-side GenericConverter so client + server stay in sync.
  let q: any = db
    .collection(SIGNATURE_DOCUMENTS_COLLECTION_NAME)
    // Generic parameter left as `any` — the GenericConverter's constraint
    // `Partial<BaseDocument>` requires createdTime: Date, but our
    // SignatureDocument types createdTime as ISO string (matches the REST
    // shape). normalizeFirestoreTimestamps below handles the runtime
    // conversion; the cast just bypasses the generic mismatch.
    .withConverter(GenericConverter<any>() as any)
    .where("ownerUserId", "==", ownerUserId);

  if (options.status) q = q.where("status", "==", options.status);
  if (options.templateId) q = q.where("templateId", "==", options.templateId);
  if (options.dateFrom) q = q.where("createdTime", ">=", options.dateFrom);
  if (options.dateTo) q = q.where("createdTime", "<=", options.dateTo);
  q = q.orderBy("createdTime", "desc");
  if (options.limit) q = q.limit(options.limit);

  return q.onSnapshot(
    (snapshot) => {
      const docs = snapshot.docs
        .map((d) => normalizeFirestoreTimestamps(d.data() as SignatureDocument))
        .filter((d) => !d.archivedAt);
      onChange(docs);
    },
    (err) => {
      // eslint-disable-next-line no-console
      console.error("subscribeToDocuments failed:", err);
      if (onError) onError(err as unknown as Error);
    }
  );
};

/**
 * Firestore realtime subscription for a SINGLE signature document. Used by
 * the detail panel so signer statuses, WhatsApp delivery updates, and the
 * signed-PDF URL appear live without polling or refreshing. Invokes
 * `onChange(null)` if the document disappears (archived / deleted).
 */
export const subscribeToDocument = (
  documentId: string,
  onChange: (document: SignatureDocument | null) => void,
  onError?: (error: Error) => void
): (() => void) => {
  return db
    .collection(SIGNATURE_DOCUMENTS_COLLECTION_NAME)
    // Same converter as the list subscription so the single-doc path
    // returns `{...data, id, path}` instead of raw data without id.
    .withConverter(GenericConverter<any>() as any)
    .doc(documentId)
    .onSnapshot(
      (snap) => {
        if (!snap.exists) {
          onChange(null);
          return;
        }
        const raw = snap.data() as SignatureDocument;
        onChange(normalizeFirestoreTimestamps(raw));
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.error(`subscribeToDocument(${documentId}) failed:`, err);
        if (onError) onError(err as unknown as Error);
      }
    );
};
